/**
 * Permission Highlighter Utility
 * 用于高亮显示页面中使用权限控制的元素
 */

interface HighlightInfo {
  element: HTMLElement;
  permissionKey: string;
  type: 'directive' | 'v-if' | 'react-component';
}

class PermissionHighlighter {
  private highlightedElements: HighlightInfo[] = [];
  private isHighlighting = false;
  private styleId = 'permission-highlight-style';

  private PermissionList: string[] = [];

  /**
   * 查找所有使用权限控制的元素
   */
  findPermissionElements(): HighlightInfo[] {
    const elements: HighlightInfo[] = [];
    // 清空权限列表
    this.clearPermissionList();

    // 1. 查找所有使用 v-permission 指令的元素（通过 data-permission-key 属性）
    const directiveElements = document.querySelectorAll(
      '[data-permission-key][data-permission-type="directive"]'
    );
    directiveElements.forEach((el) => {
      const permissionKey = el.getAttribute('data-permission-key');
      if (permissionKey && el instanceof HTMLElement) {
        this.PermissionList.push(permissionKey);
        elements.push({
          element: el,
          permissionKey,
          type: 'directive',
        });
      }
    });

    // 2. 查找使用 React Permission 组件的元素
    const reactElements = document.querySelectorAll(
      '[data-permission-key][data-permission-type="react-component"]'
    );
    reactElements.forEach((el) => {
      const permissionKey = el.getAttribute('data-permission-key');
      if (permissionKey && el instanceof HTMLElement) {
        this.PermissionList.push(permissionKey);
        elements.push({
          element: el,
          permissionKey,
          type: 'react-component',
        });
      }
    });

    // 3. 查找使用 v-if="$hasPermission(...)" 的元素
    // 通过查找所有可能的元素，检查其 Vue 实例
    //this.findVIfPermissionElements(elements);

    return elements;
  }

  /**
   * 查找使用 v-if="$hasPermission(...)" 的元素
   * 通过遍历 Vue 实例树来查找
   */
  // private findVIfPermissionElements(elements: HighlightInfo[]): void {
  //   // 获取所有 Vue 实例
  //   const vueInstances = this.getAllVueInstances();

  //   vueInstances.forEach((vm) => {
  //     if (!vm.$el) return;

  //     // 遍历组件的所有子元素
  //     const walker = document.createTreeWalker(
  //       vm.$el as Node,
  //       NodeFilter.SHOW_ELEMENT,
  //       null
  //     );

  //     let node: Node | null;
  //     while ((node = walker.nextNode())) {
  //       const element = node as HTMLElement;

  //       // 检查元素是否已经有标记
  //       if (element.hasAttribute('data-permission-key')) {
  //         continue;
  //       }

  //       // 尝试从 Vue 实例的渲染函数中查找权限信息
  //       // 这是一个启发式方法，可能不够准确
  //       // 更可靠的方法是在模板编译时添加标记
  //     }
  //   });
  // }

  /**
   * 获取所有 Vue 实例
   */
  // private getAllVueInstances(): any[] {
  //   const instances: any[] = [];

  //   // 从根实例开始遍历
  //   const rootInstance = (window as any).__VUE_ROOT_INSTANCE__;
  //   if (rootInstance) {
  //     this.collectVueInstances(rootInstance, instances);
  //   }

  //   // 也可以通过 document 查找所有元素，检查是否有 __vue__ 属性
  //   const allElements = document.querySelectorAll('*');
  //   allElements.forEach((el) => {
  //     const vm = (el as any).__vue__;
  //     if (vm && !instances.includes(vm)) {
  //       instances.push(vm);
  //     }
  //   });

  //   return instances;
  // }

  /**
   * 收集 Vue 实例
   */
  // private collectVueInstances(vm: any, instances: any[]): void {
  //   if (!vm || instances.includes(vm)) return;

  //   instances.push(vm);

  //   // 遍历子组件
  //   if (vm.$children) {
  //     vm.$children.forEach((child: any) => {
  //       this.collectVueInstances(child, instances);
  //     });
  //   }
  // }

  /**
   * 高亮显示权限控制的元素
   * @param permissionKey - 权限点，必需参数
   */
  highlight(permissionKey: string): void {
    if (!permissionKey) {
      console.warn('Permission key is required');
      return;
    }

    // 查找所有权限控制的元素
    const allElements = this.findPermissionElements();

    // 过滤出指定权限点的元素
    const elements = allElements.filter(
      (el) => el.permissionKey === permissionKey
    );

    if (elements.length === 0) {
      console.warn(`No elements found for permission: ${permissionKey}`);
      return;
    }

    // 检查是否已经高亮过这些元素
    const newElements = elements.filter((el) => {
      return !this.highlightedElements.some(
        (highlighted) => highlighted.element === el.element
      );
    });

    if (newElements.length === 0) {
      // 如果已经全部高亮，则取消高亮
      this.clearPermissionHighlight(permissionKey);
      return;
    }

    // 添加高亮样式（如果还没有添加）
    // this.addHighlightStyle();

    // 为每个新元素添加高亮
    newElements.forEach(({ element, permissionKey: key, type }) => {
      element.classList.add('g-permission-highlight');
    });

    // 将新元素添加到高亮列表
    this.highlightedElements.push(...newElements);
    this.isHighlighting = true;

    // 滚动到第一个新元素
    if (newElements.length > 0) {
      newElements[0].element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  /**
   * 清除指定权限点的高亮
   * @param permissionKey - 权限点，如果提供则只清除该权限点的高亮
   */
  clearPermissionHighlight(permissionKey: string): void {
    if (!permissionKey) {
      console.warn('Permission key is required');
      return;
    }

    // 找出需要清除高亮的元素
    const elementsToClear = this.highlightedElements.filter(
      (el) => el.permissionKey === permissionKey
    );

    if (elementsToClear.length === 0) {
      console.warn(
        `No highlighted elements found for permission: ${permissionKey}`
      );
      return;
    }

    // 清除这些元素的高亮
    elementsToClear.forEach(({ element }) => {
      if (element && element.parentNode) {
        element.classList.remove('g-permission-highlight');
      }
    });

    // 从高亮列表中移除
    this.highlightedElements = this.highlightedElements.filter(
      (el) => el.permissionKey !== permissionKey
    );

    // 如果没有高亮的元素了，更新状态
    if (this.highlightedElements.length === 0) {
      this.isHighlighting = false;
      // this.removeHighlightStyle();
    }
  }

  /**
   * 清除所有高亮
   */
  clearHighlight(): void {
    this.highlightedElements.forEach(({ element }) => {
      if (element && element.parentNode) {
        element.classList.remove('g-permission-highlight');
      }
      // element.removeAttribute('data-highlighted');
      // element.removeAttribute('data-highlighted-permission');
      // element.removeAttribute('title');
      // element.style.outline = '';
      // element.style.outlineOffset = '';
      // element.style.backgroundColor = '';
      // element.style.transition = '';
      // element.style.position = '';
      // element.style.zIndex = '';
    });

    this.highlightedElements = [];
    this.isHighlighting = false;
    // this.removeHighlightStyle();

    console.log('Cleared all permission highlights');
  }

  /**
   * 添加高亮样式
   */
  private addHighlightStyle(): void {
    if (document.getElementById(this.styleId)) {
      return;
    }

    const style = document.createElement('style');
    style.id = this.styleId;
    style.textContent = `
      .permission-highlight {
        animation: permission-pulse 2s ease-in-out infinite;
      }
      
      @keyframes permission-pulse {
        0%, 100% {
          outline-color: #ff4d4f;
          background-color: rgba(255, 77, 79, 0.1);
        }
        50% {
          outline-color: #ff7875;
          background-color: rgba(255, 77, 79, 0.15);
        }
      }
      
      .permission-highlight:hover {
        outline-color: #ff7875;
        background-color: rgba(255, 77, 79, 0.2);
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * 移除高亮样式
   */
  // private removeHighlightStyle(): void {
  //   const style = document.getElementById(this.styleId);
  //   if (style) {
  //     style.remove();
  //   }
  // }

  /**
   * 获取高亮状态
   */
  getHighlightingState(): boolean {
    return this.isHighlighting;
  }

  /**
   * 获取高亮的元素数量
   */
  getHighlightedCount(): number {
    return this.highlightedElements.length;
  }

  /**
   * 获取高亮的元素列表
   */
  getHighlightedElements(): HighlightInfo[] {
    return [...this.highlightedElements];
  }

  /**
   * 获取权限列表
   */
  getPermissionList(): string[] {
    return [...this.PermissionList];
  }
  clearPermissionList(): void {
    this.PermissionList = [];
  }
}

// 导出单例
export const permissionHighlighter = new PermissionHighlighter();
